<template>
  <div>
    <!-- First row to show the field disabled -->
    <RsFormInput
      v-if="!useDropdown"
      :id="`${id}-disabled-field`"
      v-model="title"
      :label="label"
      class="mb-0"
      :disabled="true"
      type="text"
      @input="$emit('input', boundValue); $emit('update:modelValue', boundValue)"
    />

    <!-- Sencond row to use dropdown instead -->
    <RsFormSelect
      v-else
      :id="`${id}-select-redirect`"
      :value="boundValue"
      :empty-value="null"
      :label="label"
      :disabled="disabled"
      class="border-bottom-0 mb-0"
      :options="[
        {
          value: null,
          text: 'Select',
        },
        ...redirectOptions,
      ]"
      @input="$emit('input', $event); $emit('update:modelValue', $event)"
    />
    <div
      v-if="hasRedirect"
      class="d-flex"
    >
      <RsButton
        :id="`${id}-redirect-button`"
        class="w-100 text-start input-group-text border-top-0 border-right-0 rounded-0 redirect pr-0 font-weight-bold"
        variant="light"
        :rounded="true"
        @click="handleRedirect"
      >
        {{ getTitle }}
      </RsButton>
      <span
        class="input-group-text border-left-0 border-top-0 redirect rounded-0 cursor-pointer"
        @click="handleRedirect"
      >
        <RsIcon
          icon="arrow-up-right-from-square"
          class="font-weight-bold"
          weight="far"
        />
      </span>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { navigateToNewTab } from '@/shared/helpers/navigate-new-tab';

export default defineComponent({
  name: 'RsPageRedirect',

  props: {
    id: {
      type: String,
      default: 'redirect',
    },
    /**
     * When using a single input the disable field needs a title
    */
    title: {
      type: String,
      default: 'Disabled Input',
    },
    label: {
      type: String,
      default: '',
    },
    /*
     * When using dropdown, this array must use the value for each route name
    */
    redirectOptions: {
      type: Array,
      default: () => [],
    },
    /**
     * Boolean to determine a single seleciton or dropdown selection
    */
    useDropdown: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    /*
     * The button title and URL redirect is determined on this function
    */
    redirectTo: {
      type: Function,
      default: (selectedValue?: number) => (
        {
          title: 'Go to Property',
          redirect: selectedValue ? `/properties/${selectedValue}` : `/`,
        }),
    },
    value: [String, Number, Object, Boolean],
    /**
     * Vue 3 v-model binding. Kept alongside the legacy `value`/`input` pair so both
     * `v-model` (modelValue/update:modelValue) and explicit `:value`+`@input` consumers
     * work. `undefined` means the parent did not bind v-model (see `boundValue`).
     */
    modelValue: {
      type: [String, Number, Object, Boolean],
      default: undefined,
    },
  },

  emits: ['input', 'update:modelValue'],

  data() {
    return {
      defaultTitle: 'Go To Selection' as string,
    };
  },

  computed: {
    /**
     * The externally-bound value, preferring the Vue 3 `modelValue` (v-model)
     * when present and falling back to the legacy `value` prop otherwise.
     */
    boundValue(): string | number | boolean | Record<string, any> | undefined {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
    /**
     * Based on the redirect function use its title or the default if none
    */
    getTitle(): string {
      const { title } = this.redirectTo();
      return title || this.defaultTitle;
    },
    /**
     * When using the dropdown, only show the redirect if an option is selected
    */
    hasRedirect(): boolean {
      return (this.boundValue !== undefined && this.boundValue !== null);
    },
  },

  methods: {
    /**
     * Based on the redirectTo function passed, gets the redirect link
     * either open an externa tab basedon the htttp link or a new router
    */
    handleRedirect(): void {
      const goTo = this.redirectTo(this.boundValue).redirect;
      if (goTo.startsWith('http')) {
        navigateToNewTab(goTo);
      } else {
        const route = this.$router.resolve(
          {
            path: this.redirectTo(this.boundValue).redirect,
          },
        );
        window.open(route.href, '_blank');
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';
.redirect {
    background-color: $secondary-light !important;
    color: $secondary-color !important;
    max-height: 26px !important;
    font-size: 13px !important;
    justify-content: flex-start;
    &:hover {
      color: $secondary-color;
      background-color: $secondary-light;
    }
}

.rs-input .input-group .form-control:disabled {
  border-radius: 0.25rem 0.25rem 0 0;
  background: $tertiary-dark-gray;
}
.rs-select .input-group .custom-select {
  border-radius: 0.25rem 0.25rem 0 0;
}
</style>
